import React, { useEffect, useState, useRef } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { WaitlistCTA } from './WaitlistCTA';
import { FloatingCtaProvider, useFloatingCtaState } from './floatingCtaContext';
import { FEATURE_MENU_GROUPS, getFeatures } from '../lib/features';
import { FeatureIcon } from './FeatureIcon';
// Map top-level nav labels to their live routes.
// NOTE: Enterprise, Security and Resources/Blog were moved to the noindex
// /unlisted namespace, so they're intentionally NOT linked from the nav.
const NAV_ROUTES: Record<string, string> = {
  Pricing: '/pricing',
  ChatGPT: '/chatgpt',
  Integrations: '/integrations',
  'Become an Affiliate': 'https://partners.dub.co/getgiga/affiliate-program'
};
// Nav labels whose target is an EXTERNAL site — rendered as a real anchor that
// opens in a new tab rather than an internal router Link.
const EXTERNAL_NAV = new Set(['Become an Affiliate']);
// Labels shown in the home-page top nav.
const NAV_LABELS = ['Features', 'Integrations', 'Pricing', 'ChatGPT'];
export function Layout() {
  return <FloatingCtaProvider data-id="element-3453" data-mp="1rkqtoexg1bhm">
      <LayoutInner data-id="element-3454" data-mp="1rlidytllbmsl" />
    </FloatingCtaProvider>;
}
function LayoutInner() {
  // While a section (e.g. the integration "plugs into your stack" beat) owns
  // its own primary CTA, it suppresses this global floating "Try Giga for free"
  // button so the two don't compete. It fades back in once that beat passes.
  const floatingCtaSuppressed = useFloatingCtaState();
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('up');
  // True when the floating mobile "Try Giga for free" CTA is sitting over an
  // ORANGE surface (the founder note, the footer, etc.) — the CTA is orange
  // too, so it would vanish against those. When that happens we flip the
  // button to a white treatment so it stays visible. Any orange section can
  // opt in by tagging its element with data-cta-invert; the footer is tagged
  // too.
  const [overOrange, setOverOrange] = useState(false);
  const footerRef = useRef<HTMLElement | null>(null);
  const lastScrollY = useRef(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openSection, setOpenSection] = useState<string | null>(null);
  const [featureMenuDismissed, setFeatureMenuDismissed] = useState(false);
  const features = getFeatures();
  const featureGroups = FEATURE_MENU_GROUPS.map(group => ({
    ...group,
    items: group.slugs.map(slug => features.find(feature => feature.slug === slug)).filter(Boolean)
  }));
  const location = useLocation();
  useEffect(() => {
    setMobileMenuOpen(false);
    setOpenSection(null);
  }, [location.pathname]);
  const toggleSection = (section: string) => setOpenSection(prev => prev === section ? null : section);
  // These routes open with a dark (#1C1510 / #1A1410) hero, so while the header
  // is still transparent (before the user scrolls) the nav must switch to a
  // light treatment — white links and a white logo — to stay legible. Once
  // scrolled, the header turns solid white and reverts to the dark-on-light
  // treatment.
  const darkHeroRoutes = ['/unlisted/compare/chatgpt', '/unlisted/security', '/demo', '/unlisted/team-workspace'];
  const isTeamWorkspace = location.pathname === '/unlisted/team-workspace';
  const isFeatureRoute = location.pathname.startsWith('/features/');
  const onDark = (darkHeroRoutes.includes(location.pathname) || isFeatureRoute) && !isScrolled;
  // Both full-screen home heroes own their primary action at the top of the
  // page, so the header uses a clear white Log in action until scrolling begins.
  const atHeroTop = (location.pathname === '/' || isTeamWorkspace) && !isScrolled;
  const navLinkClass = `text-sm font-semibold transition-colors ${onDark ? 'text-white hover:text-[#F7C9AC]' : 'text-neutral-600 hover:text-[#2B211A]'}`;
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrolled = currentScrollY > 20;
      setIsScrolled(scrolled);
      if (scrolled) {
        if (currentScrollY > lastScrollY.current + 5) {
          setScrollDirection('down');
          setMobileMenuOpen(false);
        } else if (currentScrollY < lastScrollY.current - 5) {
          setScrollDirection('up');
        }
      } else {
        setScrollDirection('up');
      }
      lastScrollY.current = currentScrollY;
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Watch every orange surface (the footer + any section tagged with
  // data-cta-invert, e.g. the founder note): when one of them is under the
  // bottom band of the viewport — exactly where the floating mobile CTA sits —
  // flip the CTA to its white treatment so the orange button doesn't blend
  // into the orange background. The negative top rootMargin shrinks the
  // observed region to just the bottom ~12% strip, so the flip happens only
  // while the CTA truly overlaps the orange surface (not the moment any part
  // of a tall orange section first peeks into view). Re-runs on route change
  // so it picks up the orange sections present on the current page.
  useEffect(() => {
    const surfaces = new Set<Element>();
    document.querySelectorAll('[data-cta-invert]').forEach(el => surfaces.add(el));
    if (footerRef.current) surfaces.add(footerRef.current);
    if (surfaces.size === 0) {
      setOverOrange(false);
      return;
    }
    const active = new Set<Element>();
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) active.add(entry.target);else active.delete(entry.target);
      });
      setOverOrange(active.size > 0);
    }, {
      // Only the bottom strip of the viewport counts — that's where the
      // floating CTA lives.
      rootMargin: '-88% 0px 0px 0px',
      threshold: 0
    });
    surfaces.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [location.pathname]);
  return <div className="min-h-screen bg-[#FAF8F4] text-[#2B211A] font-body selection:bg-[#EA580C]/20 selection:text-[#2B211A]" data-id="element-3455" data-mp="b21bil4z6zuv">
      <header className={`fixed top-0 w-full z-50 translate-y-0 transition-all duration-300 ${isScrolled ? 'bg-white/90 backdrop-blur-md border-b border-neutral-200 shadow-sm py-3' : 'bg-transparent py-5'}`} data-id="element-3456" data-mp="63hosiik5k1g">
        <div className="container mx-auto px-6 flex items-center justify-between" data-id="element-3457" data-mp="fxqbuqotkks8">
          <Link to="/" aria-label="Giga home" className="flex items-center gap-2 group" data-id="element-3458" data-mp="tt27d0asufa7">
            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/Vector.png" alt="" aria-hidden="true" className={`h-7 w-7 object-contain group-hover:scale-105 transition-transform ${onDark ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-3459" data-mp="pq5vdsfrlhf8" />

            <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className={`h-6 object-contain transition-[filter] ${onDark ? '[filter:brightness(0)_invert(1)]' : ''}`} data-id="element-3460" data-mp="1naxcho08ip9e" />
          </Link>

          <nav className="hidden md:flex items-center gap-7" data-id="element-3461" data-mp="zlcsd1schlyj">
            {NAV_LABELS.map(label => label === 'Features' ? <div key={label} className="relative group py-2" onMouseLeave={() => setFeatureMenuDismissed(false)} data-id="element-3462" data-mp="14bt7qrfq7hnj">
                  <button type="button" className={`${navLinkClass} inline-flex items-center gap-1`} aria-haspopup="menu" onFocus={() => setFeatureMenuDismissed(false)} data-id="element-3463" data-mp="pnydh6m2l58c">Features <ChevronDown size={14} data-id="element-3464" data-mp="ujx5r5air7e1" /></button>
                  <div className={`invisible absolute left-1/2 top-full w-[760px] -translate-x-1/2 translate-y-2 rounded-2xl border border-neutral-200 bg-white p-4 opacity-0 shadow-2xl transition-all duration-150 ${featureMenuDismissed ? '' : 'group-hover:visible group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:visible group-focus-within:translate-y-0 group-focus-within:opacity-100'}`} data-id="element-3465" data-mp="gnjs6793vq4u">
                    <div className="grid grid-cols-3 gap-4" role="menu" data-id="element-3466" data-mp="r3u9yco65ibt">
                      {featureGroups.map(group => <div key={group.key} data-id="element-3467" data-mp="m5sr9sunnl0v">
                        <p className="px-2 pb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-neutral-400" data-id="element-3468" data-mp="i4qste8kbczy">{group.label}</p>
                        <div className="space-y-0.5" data-id="element-3469" data-mp="dcz99nntq7jb">
                          {group.items.map(feature => feature && <Link key={feature.slug} to={`/features/${feature.slug}`} role="menuitem" onClick={() => setFeatureMenuDismissed(true)} className="flex items-center gap-2.5 rounded-xl px-2 py-2 text-left hover:bg-[#FFF4EC] transition-colors" data-id="element-3470" data-mp="8e4dll4rcohs">
                            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#EA580C]/10 text-[#EA580C]" data-id="element-3471" data-mp="3unyoyimx72tx"><FeatureIcon name={feature.icon} size={16} data-id="element-3472" data-mp="262i31sdiu3qi" /></span>
                            <span className="min-w-0 text-sm font-semibold leading-tight text-[#2B211A]" data-id="element-3473" data-mp="264kr46v45msl">{feature.menuLabel || feature.name}</span>
                          </Link>)}
                        </div>
                      </div>)}
                    </div>
                  </div>
                </div> : EXTERNAL_NAV.has(label) ? <a key={label} href={NAV_ROUTES[label]} target="_blank" rel="noopener noreferrer" className={navLinkClass} data-id="element-3474" data-mp="1meirwgf1oukk">
                  {label}
                </a> : <Link key={label} to={NAV_ROUTES[label]} className={`${navLinkClass} inline-flex items-center gap-1.5`} data-id="element-3475" data-mp="1re536kbbc0qq">
                  {label}{label === 'ChatGPT' && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#EA580C]/10 text-[#EA580C]" data-id="element-3476" data-mp="213562esxrga7">New</span>}
                </Link>)}
          </nav>

          <div className="flex items-center gap-3 md:gap-4" data-id="element-3477" data-mp="18mufec0vw432">
            {atHeroTop ?
          // Hero already shows the primary "Try Giga for free" CTA — keep a
          // quiet white Sign in button here until the user scrolls past it.
          // While Giga is gated, Login opens the waitlist too (the /login
          // page is preserved for later — see App.tsx route).
          <WaitlistCTA analyticsLabel="nav-sign-in" spinnerClassName="text-[#2B211A]" className="hidden md:block bg-white hover:bg-[#FFF4EC] text-[#2B211A] border border-white/80 px-5 py-2.5 rounded-full text-sm font-bold transition-colors shadow-sm" data-id="element-3478" data-mp="spqrior4rgey">
                {isTeamWorkspace ? 'Log in' : 'Sign in'}
              </WaitlistCTA> : <>
                {/* Login is waitlisted for now �� opens the waitlist instead of
                 the (preserved) /login page. */}
                <WaitlistCTA analyticsLabel="nav-log-in" spinnerClassName="text-[#2B211A]" className={`hidden md:block ${navLinkClass}`} data-id="element-3479" data-mp="etlnb8mmsmc7">
                  Log in
                </WaitlistCTA>
                <WaitlistCTA analyticsLabel="nav-try-giga" className="hidden md:block bg-[#EA580C] hover:bg-[#C2410C] text-white px-5 py-2.5 rounded-full text-sm font-bold transition-colors shadow-sm" data-id="element-3480" data-mp="3ifkjqixg3gtn">
                  Try Giga for free
                </WaitlistCTA>
              </>}

            {/* Mobile control: hamburger, plus a compact "Try for free" CTA.
             The header is only revealed while scrolling UP (it slides away on
             scroll-down), so showing the CTA here gives the user a quick
             conversion tap right next to the menu when they scroll back up.
             Hidden at the very top of the hero, where the hero already shows
             its own primary CTA. */}
            <div className="md:hidden flex items-center gap-2" data-id="element-3481" data-mp="17v367ha3tthb">
              {isScrolled && <WaitlistCTA analyticsLabel="mobile-try-free" className="bg-[#EA580C] hover:bg-[#C2410C] text-white px-4 py-2 rounded-full text-sm font-bold transition-colors shadow-sm whitespace-nowrap" data-id="element-3482" data-mp="12x1mqv8rns5b">
                  Try for free
                </WaitlistCTA>}
              <button className={`p-2 -mr-2 transition-colors ${onDark ? 'text-white' : 'text-[#2B211A]'}`} onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle mobile menu" aria-expanded={mobileMenuOpen} data-id="element-3483" data-mp="ja8anegq94dz">
                {mobileMenuOpen ? <X size={24} data-id="element-3484" data-mp="aauwi8hnrrmx" /> : <Menu size={24} data-id="element-3485" data-mp="ab3ci992vd2c" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Dropdown - Compact */}
        {mobileMenuOpen && <div className="md:hidden absolute top-full left-0 w-full bg-white border-b border-neutral-200 shadow-lg pb-3 px-5 flex flex-col gap-1.5 z-50 max-h-[80vh] overflow-y-auto" data-id="element-3486" data-mp="121hlbr28hz75">
            <div className="sticky top-0 -mx-5 px-5 pt-3 pb-3 bg-white border-b border-neutral-100 flex gap-3 z-10" data-id="element-3487" data-mp="t8xpszh8oaqj">
              {/* Login is waitlisted for now — opens the waitlist instead of
               the (preserved) /login page. */}
              <WaitlistCTA analyticsLabel="mobile-menu-log-in" spinnerClassName="text-[#2B211A]" className="flex-1 text-center bg-white text-[#2B211A] border border-neutral-200 px-4 py-2.5 rounded-lg text-sm font-bold shadow-sm" data-id="element-3488" data-mp="1ky0siath9d12">
                Log in
              </WaitlistCTA>
              <WaitlistCTA analyticsLabel="mobile-menu-try-giga" className="flex-1 text-center bg-[#EA580C] text-white px-4 py-2.5 rounded-lg text-sm font-bold shadow-sm" data-id="element-3489" data-mp="35gtjzyewt7r1">
                Try Giga for free
              </WaitlistCTA>
            </div>
            {NAV_LABELS.map(label => label === 'Features' ? <div key={label} className="border-b border-neutral-100" data-id="element-3490" data-mp="2ri3m4gk9n41m">
                <button type="button" onClick={() => toggleSection('features')} className="w-full py-2 flex items-center justify-between text-base font-medium text-[#2B211A]" aria-expanded={openSection === 'features'} data-id="element-3491" data-mp="2mja8mo4zbd1g">
                  Features <ChevronDown size={17} className={`transition-transform ${openSection === 'features' ? 'rotate-180' : ''}`} data-id="element-3492" data-mp="2wf3ifzrbcsfj" />
                </button>
                {openSection === 'features' && <div className="space-y-3 pb-3" data-id="element-3493" data-mp="bpe6buhzvkqj">
                  {featureGroups.map(group => <div key={group.key} data-id="element-3494" data-mp="2hf9gypa1a40q">
                    <p className="px-1 pb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-400" data-id="element-3495" data-mp="2hgkh8f49stn3">{group.label}</p>
                    <div className="grid grid-cols-2 gap-1" data-id="element-3496" data-mp="2qfe9jtakixuu">
                      {group.items.map(feature => feature && <Link key={feature.slug} to={`/features/${feature.slug}`} className="flex items-center gap-2 rounded-lg px-2 py-2 text-sm font-medium text-[#655B54] hover:bg-[#FFF4EC] hover:text-[#2B211A]" data-id="element-3497" data-mp="2vehfdfydh83v">
                        <FeatureIcon name={feature.icon} size={15} className="shrink-0 text-[#EA580C]" data-id="element-3498" data-mp="35cf4e5o238xj" /> <span data-id="element-3499" data-mp="35ac6kzgnwbi9">{feature.menuLabel || feature.name}</span>
                      </Link>)}
                    </div>
                  </div>)}
                </div>}
              </div> : EXTERNAL_NAV.has(label) ? <a key={label} href={NAV_ROUTES[label]} target="_blank" rel="noopener noreferrer" className="text-base font-medium text-[#2B211A] py-1.5 border-b border-neutral-100 first:mt-2" data-id="element-3500" data-mp="3j43gwa27vzf1">
                  {label}
                </a> : <Link key={label} to={NAV_ROUTES[label]} className="text-base font-medium text-[#2B211A] py-1.5 border-b border-neutral-100 first:mt-2 flex items-center gap-2" data-id="element-3501" data-mp="21z5bilqc6la">
                  {label}{label === 'ChatGPT' && <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-[#EA580C]/10 text-[#EA580C]" data-id="element-3502" data-mp="3o9ypwl5xvylf">New</span>}
                </Link>)}
          </div>}
      </header>

      <main className="flex-1" data-id="element-3503" data-mp="kqvq8vv8lmqu">
        <Outlet data-id="element-3504" data-mp="kub4jwh36rr7" />
      </main>

      {/* Global floating "Try Giga for free" CTA — mobile only. Gives the user
                                    a persistent conversion tap as they scroll, even while the header is slid
                                    away on scroll-down. Hidden at the very top of the hero (where the hero
                                    owns its own primary CTA) and while an in-page section suppresses it.
                                    Over the orange footer it switches to a white treatment so it stays
                                    visible. */}
      <AnimatePresence data-id="element-3505" data-mp="2t0lozixirqlq">
        {isScrolled && !atHeroTop && !floatingCtaSuppressed ? <motion.div initial={{
        opacity: 0,
        y: 24
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 24
      }} transition={{
        duration: 0.25,
        ease: 'easeOut'
      }} className="md:hidden fixed inset-x-0 bottom-0 z-40 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 pointer-events-none" data-id="element-3506" data-mp="2xxlmv6k7tn1r">
            <WaitlistCTA analyticsLabel="floating-cta" spinnerClassName={overOrange ? 'text-[#EA580C]' : 'text-white'} className={['pointer-events-auto inline-flex items-center justify-center px-6 py-2.5 rounded-full text-sm font-bold shadow-lg transition-colors', overOrange ? 'bg-white text-[#EA580C] shadow-black/20' : 'bg-[#EA580C] text-white shadow-[#EA580C]/30'].join(' ')} data-id="element-3507" data-mp="2f96ticmfdwxs">
              Try Giga for free
            </WaitlistCTA>
          </motion.div> : null}
      </AnimatePresence>

      <footer ref={footerRef} data-cta-invert="true" className="relative overflow-hidden bg-[#2B211A] text-sm text-white/80 pt-20 pb-40 md:pb-56" data-id="element-3508" data-mp="1o1yl25spwfj5">
        {/* Subtle grid texture */}
        <div className="dot-matrix opacity-20" aria-hidden="true" data-id="element-3509" data-mp="1xr00nuvcouwl" />

        {/* Oversized ghosted brand wordmark — sits in the lower band of the
         footer (below the sitemap), gently bleeding off the bottom edge. */}
        <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga-wordmark.svg" alt="" aria-hidden="true" className="pointer-events-none select-none absolute inset-x-0 -bottom-[2vw] mx-auto w-[80%] max-w-none object-contain opacity-[0.12] [filter:brightness(0)_invert(1)]" data-id="element-3510" data-mp="26q3hstayj08g" />

        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-3511" data-mp="mb1kl71r98lv">
          {/* Sitemap. The three feature groups used to stack inside a single
           "Features" column, which ran ~17 links deep and left the other
           three columns empty for most of the footer's height. Each group is
           now its own column, so the row reads as five balanced lists. */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-10 md:gap-8 pb-16 border-b border-white/20" data-id="element-3512" data-mp="5945phhf0hub">
            {/* Brand column */}
            <div className="col-span-2 md:col-span-3 lg:col-span-1" data-id="element-3513" data-mp="a7nwpwav8jtj">
              <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/c9cswf4udjck7zyoebqqwd/63a18a4c-5d20-4ad2-ba5f-9334c775fe47/giga4x.png" alt="Giga" className="h-7 object-contain mb-4 [filter:brightness(0)_invert(1)]" data-id="element-3514" data-mp="38qmfvvknu0xw" />

              <p className="text-white/70 leading-relaxed max-w-xs" data-id="element-3515" data-mp="3iq3k08k9k6rr">
                Everyone's new favorite colleague.
              </p>
            </div>

            {/* One column per feature group, so no single list runs long. */}
            {featureGroups.map(group => <div key={group.key} data-id="element-3516" data-mp="12vjpipxbdrl1">
                <h3 className="mb-4 text-[11px] font-bold uppercase tracking-[1.5px] text-white/60" data-id="element-3517" data-mp="12yz31obcaskc">
                  {group.label}
                </h3>
                <ul className="space-y-2.5" data-id="element-3518" data-mp="2igvgi5iphkr6">
                  {group.items.map(feature => feature && <li key={feature.slug} data-id="element-3519" data-mp="2doc1r58a8jks">
                      <Link to={`/features/${feature.slug}`} className="group/footer inline-flex items-center gap-2 text-white/80 transition-colors hover:text-white" data-id="element-3520" data-mp="2dn9ed7a64z9m">
                        <FeatureIcon name={feature.icon} size={14} className="shrink-0 text-white/55 transition-colors group-hover/footer:text-white" data-id="element-3521" data-mp="23rz7o68ts3pi" />
                        <span data-id="element-3522" data-mp="1zrp1otl19dan">{feature.menuLabel || feature.name}</span>
                      </Link>
                    </li>)}
                </ul>
              </div>)}

            {/* Product and Legal share the last column — together they are
             about the same depth as one feature group. */}
            <div className="space-y-8" data-id="element-3523" data-mp="3p1wktenn0jlb">
              {[{
              title: 'Product',
              links: [{
                label: 'Pricing',
                to: '/pricing'
              }, {
                label: 'Developers',
                to: '/developers'
              }]
            }, {
              title: 'Legal',
              links: [{
                label: 'Support',
                to: '/support'
              }, {
                label: 'Privacy',
                to: '/privacy'
              }, {
                label: 'Terms',
                to: '/terms'
              }, {
                label: 'Vulnerability Disclosure',
                to: '/vulnerability-disclosure'
              }]
            }].map(col => <div key={col.title} data-id="element-3524" data-mp="3j9s0mma15udo">
                  <h3 className="mb-4 text-[11px] font-bold uppercase tracking-[1.5px] text-white/60" data-id="element-3525" data-mp="3jatltjkrfx94">
                    {col.title}
                  </h3>
                  <ul className="space-y-2.5" data-id="element-3526" data-mp="1hjgjn2a6g3gr">
                    {col.links.map(link => <li key={link.label} data-id="element-3527" data-mp="1ck3x6in7wyep">
                        <Link to={link.to} className="text-white/80 transition-colors hover:text-white" data-id="element-3528" data-mp="1clf1cjkks86c">
                          {link.label}
                        </Link>
                      </li>)}
                  </ul>
                </div>)}
            </div>
          </div>

          {/* Bottom bar */}
          <div className="pt-8 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-3529" data-mp="1tur4wgbxvqvb">
            <div className="text-white/60" data-id="element-3530" data-mp="1yr7qp3vo1t58">
              © {new Date().getFullYear()} Tonic Labs Limited. All rights reserved.
            </div>
            <div className="flex gap-6" data-id="element-3531" data-mp="163vdvlfb4zp4">
              {[{
              label: 'Twitter',
              href: 'https://x.com/askgiga'
            }].map(social => <a key={social.label} href={social.href} target="_blank" rel="noopener noreferrer" className="text-white/80 hover:text-white transition-colors" data-id="element-3532" data-mp="1b26ohydffe8e">
                  {social.label}
                </a>)}
            </div>
          </div>
        </div>
      </footer>
    </div>;
}